import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import Header from "./layout/Header";
import BrokerDashboard from "./dashboards/BrokerDashboard";
import UnderwriterDashboard from "./dashboards/UnderwriterDashboard";  
import AdminDashboard from "./dashboards/AdminDashboard";
import BorrowerDashboard from "./dashboards/BorrowerDashboard";
import { Flag, X } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ImportantMessagesModal = ({ count, onClose }) => {
  if (count <= 0) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="important-messages-modal">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 relative animate-important-glow">
        <div className="flex flex-col items-center text-center gap-4">
          <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center">
            <Flag className="h-8 w-8 text-yellow-500" fill="currentColor" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900 mb-1">Important Messages</h2>
            <p className="text-slate-600">
              You have <span className="font-bold text-yellow-600 text-lg">{count}</span> unread important {count === 1 ? 'message' : 'messages'} that {count === 1 ? 'requires' : 'require'} your attention.
            </p>
          </div>
          <Button
            onClick={onClose}
            className="bg-[#0E333F] hover:bg-[#081120] text-white px-8 py-2 mt-2 font-semibold"
            data-testid="important-messages-ok"
          >
            OK
          </Button>
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { selectedRole, user, token } = useAuth();
  const [importantCount, setImportantCount] = useState(0);
  const [showImportantModal, setShowImportantModal] = useState(false);

  useEffect(() => {
    if (!token || !user) return;
    const checkImportantMessages = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/messages/important-count`);
        const count = res.data.count || 0;
        if (count > 0) {
          setImportantCount(count);
          setShowImportantModal(true);
        }
      } catch (error) { /* silent */ }
    };
    checkImportantMessages();
  }, [token, user]);

  const getDashboardTitle = () => {
    switch (selectedRole) {
      case 'broker':
        return 'Broker Dashboard';
      case 'underwriter':
        return 'Underwriter Dashboard';
      case 'jr_underwriter':
        return 'Jr Underwriter Dashboard';
      case 'admin':
        return 'Admin Dashboard';
      case 'borrower':
        return 'Borrower Dashboard';
      default:
        return 'Dashboard';
    }
  };

  const renderDashboard = () => {
    switch (selectedRole) {
      case 'broker':
        return <BrokerDashboard />;
      case 'underwriter':
      case 'jr_underwriter':
        return <UnderwriterDashboard />;
      case 'admin':
        return <AdminDashboard />;
      case 'borrower':
        return <BorrowerDashboard />;
      default:
        return (
          <div className="p-6 text-center">
            <p className="text-slate-600">Please select a role to continue.</p>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#F8FAFC' }}>
      <Header title={getDashboardTitle()} />
      {showImportantModal && (
        <ImportantMessagesModal
          count={importantCount}
          onClose={() => setShowImportantModal(false)}
        />
      )}
      {renderDashboard()}
    </div>
  );
};

export default Dashboard;
